import { normalizeProduct } from "../inventory/api";
import {
  CollectionTaskPredecessor,
  CommissionPayoutDetail,
  Task,
  TaskAsset,
  TaskStatus,
  TaskType,
} from "../../types";
import { centsFromApi, optionalCentsFromApi } from "../../currency";

export const normalizeCollectionTaskPredecessors = (
  value: Record<string, any>,
): Record<string, CollectionTaskPredecessor> =>
  Object.fromEntries(
    Object.entries(value || {}).map(([key, predecessor]) => [
      key,
      {
        taskId: predecessor?.taskId ?? predecessor?.task_id ?? key,
        previousTaskId:
          predecessor?.previousTaskId ?? predecessor?.previous_task_id ?? null,
        previousGrossCents: centsFromApi(
          predecessor?.previousGrossCents ?? predecessor?.previous_gross_cents,
        ),
        previousScheduledFor:
          predecessor?.previousScheduledFor ??
          predecessor?.previous_scheduled_for ??
          null,
        days: predecessor?.days ?? null,
        previousDays: predecessor?.previousDays ?? predecessor?.previous_days ?? null,
      },
    ]),
  );

export const normalizeCommissionPayoutDetail = (
  value: Record<string, any>,
): CommissionPayoutDetail => ({
  payoutId: value.payoutId ?? value.payout_id ?? "",
  payoutCents: optionalCentsFromApi(value.payoutCents ?? value.payout_cents) ?? 0,
  paymentType: value.paymentType ?? value.payment_type,
  status: value.status ?? "",
  reason: value.reason ?? "",
  previousOutstandingCommissionCents:
    optionalCentsFromApi(
      value.previousOutstandingCommissionCents ??
        value.previous_outstanding_commission_cents,
    ) ?? 0,
  outstandingCommissionCents:
    optionalCentsFromApi(
      value.outstandingCommissionCents ?? value.outstanding_commission_cents,
    ) ?? 0,
  paidAt: value.paidAt ?? value.paid_at ?? "",
  allocations: Array.isArray(value.allocations)
    ? value.allocations.map((allocation: Record<string, any>) => ({
        accrualId: allocation.accrualId ?? allocation.accrual_id ?? "",
        sourceTaskId:
          allocation.sourceTaskId ?? allocation.source_task_id ?? null,
        kind: allocation.kind ?? "",
        periodStart: allocation.periodStart ?? allocation.period_start ?? "",
        periodEnd: allocation.periodEnd ?? allocation.period_end ?? "",
        earnedCents:
          optionalCentsFromApi(
            allocation.earnedCents ?? allocation.earned_cents,
          ) ?? 0,
        allocatedCents:
          optionalCentsFromApi(
            allocation.allocatedCents ?? allocation.allocated_cents,
          ) ?? 0,
        paymentType: allocation.paymentType ?? allocation.payment_type,
      }))
    : [],
});

type ApiTaskAsset = {
  id: string;
  taskId?: string;
  task_id?: string;
  type?: string | null;
  url?: string | null;
  createdAt?: string;
  created_at?: string;
  uploadedBy?: string | null;
  uploaded_by?: string | null;
};

type ApiTask = {
  correctionOfId?: string | null;
  correctedById?: string | null;
  correction_of_id?: string | null;
  corrected_by_id?: string | null;
  id: string;
  executionTaskId?: string;
  execution_task_id?: string;
  title?: string;
  type?: string;
  status?: string;
  is_public?: boolean;
  creator?: string | null;
  organization?: string | null;
  machine?: string | null;
  machineName?: string | null;
  machine_name?: string | null;
  serviceTaskId?: string | null;
  service_task_id?: string | null;
  collectionInputMode?: Task["collectionInputMode"];
  collection_input_mode?: Task["collectionInputMode"];
  creditsPerDollar?: number | null;
  credits_per_dollar?: number | null;
  location?: string | null;
  locationName?: string | null;
  location_name?: string | null;
  locationAddress?: any | null;
  location_address?: any | null;
  growthLeadId?: string | null;
  growth_lead_id?: string | null;
  workMode?: Task["workMode"];
  work_mode?: Task["workMode"];
  action?: string | null;
  customAction?: string | null;
  custom_action?: string | null;
  address?: any | null;
  latitude?: number | null;
  longitude?: number | null;
  routable?: boolean;
  assignee?: string | null;
  assigneeName?: string | null;
  assignee_name?: string | null;
  assigneeEmail?: string | null;
  assignee_email?: string | null;
  assigneePhotoUrl?: string | null;
  assignee_photo_url?: string | null;
  notes?: string | null;
  createdAt?: string;
  created_at?: string;
  scheduledFor?: string | null;
  scheduled_for?: string | null;
  startedAt?: string | null;
  started_at?: string | null;
  doneAt?: string | null;
  done_at?: string | null;
  cleanupStatus?: Task["cleanupStatus"];
  cleanup_status?: Task["cleanupStatus"];
  cleanupWarningSentAt?: string | null;
  cleanup_warning_sent_at?: string | null;
  cleanupCancellationDate?: string | null;
  cleanup_cancellation_date?: string | null;
  isLive?: boolean | null;
  is_live?: boolean | null;
  distance?: number | null;
  duration?: number | null;
  grossCents?: number | null;
  gross_cents?: number | null;
  grossCashCents?: number | null;
  gross_cash_cents?: number | null;
  grossCardCents?: number | null;
  gross_card_cents?: number | null;
  refundsCents?: number | null;
  refunds_cents?: number | null;
  commissionCents?: number | null;
  commission_cents?: number | null;
  commissionEarnedCents?: number | null;
  commission_earned_cents?: number | null;
  commissionPayoutCents?: number | null;
  commission_payout_cents?: number | null;
  previousOutstandingCommissionCents?: number | null;
  previous_outstanding_commission_cents?: number | null;
  outstandingCommissionCents?: number | null;
  outstanding_commission_cents?: number | null;
  commissionPaymentStatus?: Task["commissionPaymentStatus"];
  commission_payment_status?: Task["commissionPaymentStatus"];
  commissionPaymentReason?: string | null;
  commission_payment_reason?: string | null;
  commissionPaymentDeferred?: boolean;
  commission_payment_deferred?: boolean;
  commissionPaymentType?: Task["commissionPaymentType"];
  commission_payment_type?: Task["commissionPaymentType"];
  commissionCarryForward?: Array<Record<string, unknown>>;
  commission_carry_forward?: Array<Record<string, unknown>>;
  netCents?: number | null;
  net_cents?: number | null;
  card_transaction_count?: number | null;
  cardTransactionCount?: number | null;
  card_transaction_surcharge_total_cents?: number | null;
  cardTransactionSurchargeTotalCents?: number | null;
  financialsOverridden?: boolean | null;
  financials_overridden?: boolean | null;
  prefill_updated_at?: string | null;
  collectionWindowStart?: string | null;
  collection_window_start?: string | null;
  collectionWindowEnd?: string | null;
  collection_window_end?: string | null;
  collectionWindowBasis?: Task["collectionWindowBasis"];
  collection_window_basis?: Task["collectionWindowBasis"];
  lastVisitAt?: string | null;
  last_visit_at?: string | null;
  daysSinceLastVisit?: number | null;
  days_since_last_visit?: number | null;
  inventoryCompletion?: any;
  inventory_completion?: any;
  inventorySourceMode?: Task["inventorySourceMode"];
  inventory_source_mode?: Task["inventorySourceMode"];
  inventorySourceWarehouseId?: string | null;
  inventory_source_warehouse_id?: string | null;
  inventorySourceWarehouseName?: string | null;
  inventory_source_warehouse_name?: string | null;
  inventorySourceVanId?: string | null;
  inventory_source_van_id?: string | null;
  inventorySourceVanName?: string | null;
  destinationVanId?: string | null;
  destinationVanName?: string | null;
  destination_van_id?: string | null;
  destination_van_name?: string | null;
  inventory_source_van_name?: string | null;
  warehouseId?: string | null;
  warehouse_id?: string | null;
  warehouseName?: string | null;
  warehouse_name?: string | null;
  warehouseAddress?: any | null;
  warehouse_address?: any | null;
  refillTaskId?: string | null;
  refill_task_id?: string | null;
  refillTaskIds?: string[] | null;
  refill_task_ids?: string[] | null;
  pickupLines?: any[] | null;
  pickup_lines?: any[] | null;
  assets?: ApiTaskAsset[] | null;
  automationSource?: Task["automationSource"];
  automation_source?: Task["automationSource"];
  automationCancellationPolicy?: Task["automationCancellationPolicy"];
  automation_cancellation_policy?: Task["automationCancellationPolicy"];
  manualScheduleOverrideAt?: string | null;
  manual_schedule_override_at?: string | null;
  manualScheduleOverrideBy?: string | null;
  manual_schedule_override_by?: string | null;
  manualScheduleOverrideByName?: string | null;
  manual_schedule_override_by_name?: string | null;
  manualScheduleOverrideByEmail?: string | null;
  manual_schedule_override_by_email?: string | null;
  manualScheduleOverrideFrom?: string | null;
  manual_schedule_override_from?: string | null;
  manualScheduleOverrideSource?: Task["manualScheduleOverrideSource"];
  manual_schedule_override_source?: Task["manualScheduleOverrideSource"];
};

const normalizeTaskAsset = (asset: ApiTaskAsset): TaskAsset | null => {
  const type = (asset.type || "").toLowerCase();
  if (type !== "confirmation_photo" && type !== "photo_confirmation") {
    return null;
  }
  return {
    id: asset.id,
    taskId: asset.taskId ?? asset.task_id ?? "",
    type: "confirmation_photo",
    url: asset.url ?? null,
    createdAt: asset.createdAt ?? asset.created_at ?? "",
    uploadedBy: asset.uploadedBy ?? asset.uploaded_by ?? null,
  };
};

const hasApiField = (task: ApiTask, camelKey: keyof ApiTask, snakeKey: keyof ApiTask) =>
  Object.prototype.hasOwnProperty.call(task, camelKey) ||
  Object.prototype.hasOwnProperty.call(task, snakeKey);

export const mapTaskTypeToApi = (type?: TaskType): string | undefined => {
  if (!type) return undefined;
  return type;
};

export const mapTaskStatusToApi = (status?: TaskStatus): string | undefined => {
  if (!status) return undefined;
  return status;
};

export const mapTaskTypeFromApi = (type?: string | null): TaskType => {
  const key = (type || "").toLowerCase();
  if (!key) return "other";
  if (
    key === "default" ||
    key === "machine_remove" ||
    key === "machine_install" ||
    key === "machine_clean" ||
    key === "machine_repair" ||
    key === "machine_refill" ||
    key === "machine_refund" ||
    key === "machine_collection" ||
    key === "machine_service" ||
    key === "machine_pickup_inventory" ||
    key === "location_lead" ||
    key === "other"
  ) {
    return key as TaskType;
  }
  return "other";
};

export const mapTaskStatusFromApi = (status?: string | null): TaskStatus => {
  const key = (status || "").toLowerCase();
  if (
    key === "done" ||
    key === "pending" ||
    key === "unassigned" ||
    key === "error" ||
    key === "cancelled"
  ) {
    return key as TaskStatus;
  }
  return "pending";
};

export const normalizeTaskResponse = (
  task: ApiTask,
  options?: { partial?: boolean },
): Task => {
  const normalized: Task = {
  id: task.id,
  correctionOfId: task.correctionOfId ?? task.correction_of_id ?? null,
  correctedById: task.correctedById ?? task.corrected_by_id ?? null,
  executionTaskId: task.executionTaskId ?? task.execution_task_id,
  title: task.title ?? "",
  type: mapTaskTypeFromApi(task.type),
  status: mapTaskStatusFromApi(task.status),
  isPublic: task.is_public ?? undefined,
  creatorId: task.creator ?? null,
  organizationId: task.organization ?? null,
  userId: task.assignee ?? null,
  assigneeName: task.assigneeName ?? task.assignee_name ?? null,
  assigneeEmail: task.assigneeEmail ?? task.assignee_email ?? null,
  assigneePhotoUrl: task.assigneePhotoUrl ?? task.assignee_photo_url ?? null,
  machineId: task.machine ?? null,
  machineName: task.machineName ?? task.machine_name ?? null,
  serviceTaskId: task.serviceTaskId ?? task.service_task_id ?? null,
  collectionInputMode:
    task.collectionInputMode ?? task.collection_input_mode ?? undefined,
  creditsPerDollar:
    task.creditsPerDollar ?? task.credits_per_dollar ?? undefined,
  locationId:
    task.location ??
    ((task.growthLeadId ?? task.growth_lead_id)
      ? `growth:${task.growthLeadId ?? task.growth_lead_id}`
      : null),
  locationName:
    task.locationName ??
    task.location_name ??
    task.address?.name ??
    task.title ??
    null,
  locationAddress: task.locationAddress ?? task.location_address ?? null,
  growthLeadId: task.growthLeadId ?? task.growth_lead_id ?? null,
  workMode: task.workMode ?? task.work_mode ?? undefined,
  action: task.action ?? undefined,
  customAction: task.customAction ?? task.custom_action ?? undefined,
  address: task.address ? {
    ...task.address,
    street: task.address.street ?? task.address.addressLines?.join(", "),
    city: task.address.city ?? task.address.locality,
    state: task.address.state ?? task.address.administrativeArea,
    zip: task.address.zip ?? task.address.postalCode,
    country: task.address.country ?? task.address.countryCode,
  } : null,
  latitude: task.latitude ?? null,
  longitude: task.longitude ?? null,
  routable: task.routable,
  notes: task.notes ?? undefined,
  createdAt: task.createdAt ?? task.created_at ?? "",
  scheduledFor: task.scheduledFor ?? task.scheduled_for ?? null,
  startedAt: task.startedAt ?? task.started_at ?? undefined,
  doneAt: task.doneAt ?? task.done_at ?? undefined,
  cleanupStatus: task.cleanupStatus ?? task.cleanup_status ?? "current",
  cleanupWarningSentAt:
    task.cleanupWarningSentAt ?? task.cleanup_warning_sent_at ?? null,
  cleanupCancellationDate:
    task.cleanupCancellationDate ?? task.cleanup_cancellation_date ?? null,
  isLive: Boolean(task.isLive ?? task.is_live),
  distance: task.distance ?? undefined,
  duration: task.duration ?? undefined,
  grossCents: optionalCentsFromApi(task.grossCents ?? task.gross_cents),
  grossCashCents: optionalCentsFromApi(task.grossCashCents ?? task.gross_cash_cents),
  grossCardCents: optionalCentsFromApi(task.grossCardCents ?? task.gross_card_cents),
  refundsCents: optionalCentsFromApi(task.refundsCents ?? task.refunds_cents),
  commissionCents: optionalCentsFromApi(
    task.commissionCents ?? task.commission_cents,
  ),
  commissionEarnedCents: optionalCentsFromApi(
    task.commissionEarnedCents ?? task.commission_earned_cents,
  ),
  commissionPayoutCents: optionalCentsFromApi(
    task.commissionPayoutCents ?? task.commission_payout_cents,
  ),
  previousOutstandingCommissionCents: optionalCentsFromApi(
    task.previousOutstandingCommissionCents ??
      task.previous_outstanding_commission_cents,
  ),
  outstandingCommissionCents: optionalCentsFromApi(
    task.outstandingCommissionCents ?? task.outstanding_commission_cents,
  ),
  commissionPaymentStatus:
    task.commissionPaymentStatus ??
    task.commission_payment_status ??
    undefined,
  commissionPaymentReason:
    task.commissionPaymentReason ??
    task.commission_payment_reason ??
    undefined,
  commissionPaymentDeferred: Boolean(
    task.commissionPaymentDeferred ?? task.commission_payment_deferred,
  ),
  commissionPaymentType:
    task.commissionPaymentType ?? task.commission_payment_type ?? undefined,
  commissionCarryForward: (
    task.commissionCarryForward ?? task.commission_carry_forward ?? []
  ).map((entry: any) => ({
    sourceTaskId: entry.sourceTaskId ?? entry.source_task_id ?? null,
    date: entry.date ?? "",
    amountCents: centsFromApi(entry.amountCents ?? entry.amount_cents),
  })),
  netCents: optionalCentsFromApi(task.netCents ?? task.net_cents),
  cardTransactionCount:
    task.cardTransactionCount ?? task.card_transaction_count ?? undefined,
  cardTransactionSurchargeTotalCents: optionalCentsFromApi(
    task.cardTransactionSurchargeTotalCents ??
      task.card_transaction_surcharge_total_cents,
  ),
  financialsOverridden:
    task.financialsOverridden ?? task.financials_overridden ?? undefined,
  prefillUpdatedAt: task.prefill_updated_at ?? undefined,
  collectionWindowStart:
    task.collectionWindowStart ?? task.collection_window_start ?? null,
  collectionWindowEnd:
    task.collectionWindowEnd ?? task.collection_window_end ?? null,
  collectionWindowBasis:
    task.collectionWindowBasis === "exact_collection_time" ||
    task.collection_window_basis === "exact_collection_time"
      ? "exact_collection_time"
      : task.collectionWindowBasis || task.collection_window_basis
        ? "scheduled_day_boundary"
        : undefined,
  lastVisitAt: task.lastVisitAt ?? task.last_visit_at ?? null,
  daysSinceLastVisit:
    task.daysSinceLastVisit ?? task.days_since_last_visit ?? null,
  inventoryCompletion: hasApiField(task, "inventoryCompletion", "inventory_completion")
    ? normalizeInventoryCompletion(task.inventoryCompletion ?? task.inventory_completion)
    : undefined,
  inventorySourceMode:
    task.inventorySourceMode ?? task.inventory_source_mode ?? "warehouse",
  inventorySourceWarehouseId:
    task.inventorySourceWarehouseId ?? task.inventory_source_warehouse_id ?? null,
  inventorySourceWarehouseName:
    task.inventorySourceWarehouseName ?? task.inventory_source_warehouse_name ?? null,
  inventorySourceVanId:
    task.inventorySourceVanId ?? task.inventory_source_van_id ?? null,
  inventorySourceVanName:
    task.inventorySourceVanName ?? task.inventory_source_van_name ?? null,
  destinationVanId: task.destinationVanId ?? task.destination_van_id ?? null,
  destinationVanName: task.destinationVanName ?? task.destination_van_name ?? null,
  warehouseId: task.warehouseId ?? task.warehouse_id ?? null,
  warehouseName: task.warehouseName ?? task.warehouse_name ?? null,
  warehouseAddress: task.warehouseAddress ?? task.warehouse_address ?? null,
  refillTaskId: task.refillTaskId ?? task.refill_task_id ?? null,
  refillTaskIds: task.refillTaskIds ?? task.refill_task_ids ?? [],
  pickupLines: hasApiField(task, "pickupLines", "pickup_lines")
    ? normalizePickupLines(task.pickupLines ?? task.pickup_lines)
    : undefined,
  assets: hasApiField(task, "assets", "assets")
    ? (task.assets || [])
        .map(normalizeTaskAsset)
        .filter((asset): asset is TaskAsset => Boolean(asset))
    : undefined,
  automationSource: task.automationSource ?? task.automation_source ?? "manual",
  automationCancellationPolicy:
    task.automationCancellationPolicy ??
    task.automation_cancellation_policy ??
    undefined,
  manualScheduleOverrideAt: hasApiField(
    task,
    "manualScheduleOverrideAt",
    "manual_schedule_override_at",
  )
    ? task.manualScheduleOverrideAt ?? task.manual_schedule_override_at ?? null
    : undefined,
  manualScheduleOverrideBy: hasApiField(
    task,
    "manualScheduleOverrideBy",
    "manual_schedule_override_by",
  )
    ? task.manualScheduleOverrideBy ?? task.manual_schedule_override_by ?? null
    : undefined,
  manualScheduleOverrideByName: hasApiField(
    task,
    "manualScheduleOverrideByName",
    "manual_schedule_override_by_name",
  )
    ? task.manualScheduleOverrideByName ??
      task.manual_schedule_override_by_name ??
      null
    : undefined,
  manualScheduleOverrideByEmail: hasApiField(
    task,
    "manualScheduleOverrideByEmail",
    "manual_schedule_override_by_email",
  )
    ? task.manualScheduleOverrideByEmail ??
      task.manual_schedule_override_by_email ??
      null
    : undefined,
  manualScheduleOverrideFrom: hasApiField(
    task,
    "manualScheduleOverrideFrom",
    "manual_schedule_override_from",
  )
    ? task.manualScheduleOverrideFrom ?? task.manual_schedule_override_from ?? null
    : undefined,
  manualScheduleOverrideSource: hasApiField(
    task,
    "manualScheduleOverrideSource",
    "manual_schedule_override_source",
  )
    ? task.manualScheduleOverrideSource ??
      task.manual_schedule_override_source ??
      null
    : undefined,
  };
  if (options?.partial) {
    const omittedReportFields: (keyof Task)[] = [
      "correctionOfId",
      "correctedById",
      "creatorId",
      "organizationId",
      "isPublic",
      "automationSource",
      "automationCancellationPolicy",
      "collectionInputMode",
      "creditsPerDollar",
      "locationAddress",
      "collectionWindowStart",
      "collectionWindowEnd",
      "collectionWindowBasis",
      "lastVisitAt",
      "daysSinceLastVisit",
      "inventoryCompletion",
      "inventorySourceMode",
      "inventorySourceWarehouseId",
      "inventorySourceWarehouseName",
      "inventorySourceVanId",
      "inventorySourceVanName",
      "destinationVanId",
      "destinationVanName",
      "warehouseId",
      "warehouseName",
      "warehouseAddress",
      "refillTaskId",
      "refillTaskIds",
      "pickupLines",
      "assets",
      "prefillUpdatedAt",
      "manualScheduleOverrideAt",
      "manualScheduleOverrideBy",
      "manualScheduleOverrideByName",
      "manualScheduleOverrideByEmail",
      "manualScheduleOverrideFrom",
      "manualScheduleOverrideSource",
    ];
    omittedReportFields.forEach((key) => Reflect.deleteProperty(normalized, key));
    // Older report responses omit identity; preserve cached full-task fields in that case.
    const identityFields = { userId: ["assignee"], assigneeName: ["assigneeName", "assignee_name"],
      assigneeEmail: ["assigneeEmail", "assignee_email"], assigneePhotoUrl: ["assigneePhotoUrl", "assignee_photo_url"] };
    Object.entries(identityFields).forEach(([key, fields]) => {
      if (!fields.some(field => Object.prototype.hasOwnProperty.call(task, field))) Reflect.deleteProperty(normalized, key);
    });
  }
  return normalized;
};

const numberOrZero = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeInventoryCompletion = (completion: any): Task["inventoryCompletion"] => {
  if (!completion || !Array.isArray(completion.items)) return null;
  return {
    items: completion.items.map((line: any) => ({
      itemId: String(line?.itemId ?? line?.item_id ?? ""),
      product: normalizeProduct(line?.product || {}),
      stockBefore: numberOrZero(line?.stockBefore ?? line?.stock_before),
      refilledQuantity: numberOrZero(line?.refilledQuantity ?? line?.refilled_quantity),
      finalStock: numberOrZero(line?.finalStock ?? line?.final_stock),
      movementId: line?.movementId ?? line?.movement_id ?? null,
    })),
  };
};

const normalizePickupLines = (lines: any[] | null | undefined): Task["pickupLines"] =>
  (Array.isArray(lines) ? lines : []).map((line) => ({
    id: String(line?.id ?? ""),
    refillTaskId: line?.refillTaskId ?? line?.refill_task_id ?? null,
    product: normalizeProduct(line?.product || {}),
    machineName: line?.machineName ?? line?.machine_name ?? null,
    currentStock:
      (line?.currentStock ?? line?.current_stock) === null ||
      (line?.currentStock === undefined && line?.current_stock === undefined)
        ? null
        : numberOrZero(line?.currentStock ?? line?.current_stock),
    capacity:
      line?.capacity === null || line?.capacity === undefined
        ? null
        : numberOrZero(line?.capacity),
    suggestedQuantity: numberOrZero(line?.suggestedQuantity ?? line?.suggested_quantity),
    pickedUpQuantity:
      (line?.pickedUpQuantity ?? line?.picked_up_quantity) === null ||
      (line?.pickedUpQuantity === undefined && line?.picked_up_quantity === undefined)
        ? null
        : numberOrZero(line?.pickedUpQuantity ?? line?.picked_up_quantity),
  }));
